import * as React from 'react'
import moment from 'moment'
import { inject, observer } from 'mobx-react'
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap'
import { CurrentCompanyStore } from '../../../../../../stores/CurrentCompanyStore'
import { API } from '@getgreenline/homi-shared'
import { AuditCompleteExample } from './AuditCompleteExample'
import { AuditStore } from '../EditAuditStore'
import { parseErrorMsg } from '../../../../../../utilities/helpers'
import { message } from 'antd-v4'
import { Track } from '../../../../../../Track'
import { AuditModels, AuditProductModels } from '@getgreenline/audits'
import { ProductV2Api, ProductV2Models } from '@getgreenline/products'
import { dayjs } from '../../../../../../utilities/dayjs'
import { BlazeAlert } from '../../../../../../components/BlazeAlert/BlazeAlert'
import { Routes } from '../../../../../../constants/Routes'

interface Props {
  audit: AuditModels.CreateInventoryAudit
  auditStore: AuditStore
  onHide: () => void
  onSubmit: () => void
}

interface InjectedProps {
  currentCompanyStore?: CurrentCompanyStore
}

interface State {
  isSaving: boolean
  syncingAllRecords: boolean
  syncingSuccessful: boolean | undefined
  deletedProducts: AuditProductModels.EditAuditProduct[]
  isValidating: boolean
}

@inject('currentCompanyStore')
@observer
export class CompleteModal extends React.Component<Props & InjectedProps, State> {
  companyId = this.props.currentCompanyStore!.company!.id

  constructor(props: Props) {
    super(props)

    this.state = {
      isSaving: false,
      syncingAllRecords: false,
      syncingSuccessful: undefined,
      deletedProducts: [],
      isValidating: false,
    }
  }

  componentDidMount = async () => this.syncAllRecords()

  syncAllRecords = async () => {
    if (this.props.auditStore.unSynceRecordExists) {
      this.setState({ syncingAllRecords: true })

      try {
        await this.props.auditStore.syncAllRecords()

        message.success({
          content: 'All audit products and lots were successfully synced',
          key: 'SYNC_ALL_RECORDS',
          duration: 10,
        })

        this.setState({ syncingSuccessful: true })
      } catch (error) {
        this.setState({ syncingSuccessful: false })
        message.error({
          content: parseErrorMsg(error) || 'An error occurred while syncing products',
          key: 'SYNC_ALL_RECORDS',
          duration: 10,
        })
      } finally {
        this.setState({ syncingAllRecords: false })
      }
    }
  }

  getDeletedProducts = async () => {
    const updatedProducts = await ProductV2Api.getProducts(this.companyId, {
      updateDate: dayjs(this.props.audit.createDate).toDate(),
    })

    const mappedDeletedProducts = new Map<string, ProductV2Models.IProductContract>()

    updatedProducts.forEach((product) => {
      if (product.isDeleted) {
        mappedDeletedProducts.set(product.id, product)
      }
    })

    const foundDeletedProducts = this.props.audit.products.filter((product) => {
      return mappedDeletedProducts.has(product.productId)
    })

    return foundDeletedProducts
  }

  submit = async () => {
    this.setState({ isSaving: true })
    try {
      const auditResponse = await API.completeInventoryAudit(this.companyId, this.props.audit.id)

      Track.track('dashboard:audits_completed', auditResponse)

      this.props.audit.populate(auditResponse)

      this.props.auditStore.setEditable()

      this.props.onSubmit()
    } catch (error) {
      message.error(parseErrorMsg(error) || 'There was an error cancelling this audit')
    } finally {
      this.setState({ isSaving: false })
      this.props.onHide()
    }
  }

  preSumbitCheck = async () => {
    try {
      this.setState({ isValidating: true })

      const deletedProducts = await this.getDeletedProducts()

      this.setState({ deletedProducts })

      if (deletedProducts.length > 0) {
        return
      }

      await this.submit()
    } catch (error) {
      this.setState({ isValidating: false })
    }
  }

  get hasDeletedProducts() {
    return this.state.deletedProducts.length > 0
  }

  render() {
    const auditTime = moment(this.props.audit.createDate).fromNow()
    const productsNotNull = this.props.audit.products.filter((p) => p.enteredQuantity !== null)
    const batchesNotNull = []
    this.props.audit.products.forEach((product) => {
      const validBatches = product.batches.filter((batch) => batch.enteredQuantity !== null)
      if (validBatches.length > 0) {
        batchesNotNull.push(validBatches)
      }
    })

    let saveButton: JSX.Element
    if (this.state.isSaving) {
      saveButton = (
        <button className='btn btn-lg btn-success w-100' disabled>
          Completing...
        </button>
      )
    } else {
      if (this.state.syncingSuccessful === false) {
        saveButton = (
          <button
            className='btn btn-lg btn-warning w-100'
            onClick={this.syncAllRecords}
            disabled={this.state.syncingAllRecords}
          >
            Records out of sync. Click to retry
          </button>
        )
      } else {
        saveButton = (
          <button
            className={`btn btn-lg btn-success w-100`}
            onClick={this.preSumbitCheck}
            disabled={
              this.state.syncingAllRecords || this.hasDeletedProducts || this.state.isValidating
            }
          >
            Complete
          </button>
        )
      }
    }

    return (
      <Modal isOpen={true} toggle={() => this.props.onHide()}>
        <ModalHeader toggle={() => this.props.onHide()}>Complete your inventory audit</ModalHeader>

        <ModalBody>
          <div>
            <div>This audit started {auditTime}.</div>
            <div>
              After completion, the following items will have their inventory levels adjusted at{' '}
              {this.props.audit.locationName}.
            </div>
            <div>- {productsNotNull.length} products.</div>

            {batchesNotNull.length > 0 && <div>- {batchesNotNull.length} lots.</div>}
          </div>

          <br />
          <AuditCompleteExample
            auditCompleteMode={this.props.currentCompanyStore!.company!.auditCompleteMode}
          />
          <br />

          {saveButton}
        </ModalBody>

        {this.hasDeletedProducts && (
          <ModalFooter style={{ flexDirection: 'column' }}>
            <BlazeAlert type='error' showIcon>
              There are products that have been deleted since the audit was started. Please review
              the list below and remove them from the audit before completing.
            </BlazeAlert>

            <ul style={{ marginTop: 16 }}>
              {this.state.deletedProducts.map((product) => (
                <li key={product.productId}>
                  <a
                    href={Routes.EDIT_PRODUCT(this.companyId, product.productId)}
                    target='_blank'
                    rel='noreferrer'
                  >
                    {product.productName}
                  </a>
                </li>
              ))}
            </ul>
          </ModalFooter>
        )}
      </Modal>
    )
  }
}
